import React from 'react';
import {
  FilterCombo,
  ProgressBar,
  Renderers,
  PuzzleButton,
  PuzzleDropDownButton,
  PuzzleCheckbox,
  Container,
  PuzzleToolbar
} from "puzzle";
import DropUpload from "../../../BatchUpload/DropUpload";
import { FILE, FILE_FORMAT, DROPUPLOAD_PROPS, STATE } from "../../../BatchUpload/Types";
import { GridCellProps, GridColumn } from "@progress/kendo-react-grid";
import _ from "lodash";
import classNames from "classnames";
import { Menu, MenuItem } from '@progress/kendo-react-layout';
import WordCountLink from "../../../../util/WordCountLink/WordCountLink";
import { Keys } from "@progress/kendo-react-common";
import { DropDownButtonItemClickEvent } from '@progress/kendo-react-buttons';
import { ANNOUNCEMENTS_TYPE, getTableTdAccessibilityProps, setAccessibilityModeForMenu } from "../../../../util/Util";
import KendoGridAbstract from "../../../../util/KendoGridAbstract/KendoGridAbstract";
import { BATCH } from "../../../MultiBatchUpload/Types";

type PROPS = DROPUPLOAD_PROPS & {
  batch: BATCH,
  className: string,
  field: string,
  moveToTargets: BATCH[],
  multiBatchDialog: boolean,
  partiallyAnalyzed: boolean,
  projectFileFormats: any[],
  uploadFlowState: any,
  zIndexCls: string
}

type SOURCE_STATE = STATE & {
  editField: string,
  field: string | undefined,
  projectFileFormats: any[]
}

class SourceFiles extends DropUpload<PROPS, SOURCE_STATE> {
  renders: any;
  lastSelectedIndex = 0;

  static defaultProps = {
    partiallyAnalyzed: false,
    multiBatchDialog: false,
    isDisabled: false,
    isCollapsed: false,
    moduleName: 'DropUpload',
    uploadActionName: 'uploadFiles'
  };

  public state: any = {
    ...this.state,
    editField: '',
    extractArchive: true,
    extractArchiveEnabled: true,
    projectFileFormats: [],
    editFileTicket: null,
    movedDocuments: [],
    moduleIsUploading: false
  };
  menuRef: any;

  constructor(props: any) {
    super(props);
    this.renders = new Renderers(this.enterEdit, this.exitEdit, 'inEdit');
  }

  /**
   * @inheritDoc
   */
  componentDidUpdate = (prevProps: DROPUPLOAD_PROPS, prevState: STATE,) => {
    super.componentDidUpdate(prevProps, prevState);
    setAccessibilityModeForMenu(this.menuRef?.menuWrapperEl, this.translate('file_plural'));
  };


  /**
   * When component detects its moduleId with step 'start' in uploadFlowState, set's moduleIsUploading to true. Component is in uploading state until it receives step 'end'
   * for its moduleId then moduleIsUploading is reset to false.
   *
   * @param props
   * @param state
   */
  static getDerivedStateFromProps(props: any, state: any) {
    let derivedState = { ...state };
    if (!_.isEmpty(props.uploadFlowState)) {
      const start = {
        uploadGroupId: props.moduleId,
        step: 'start'
      };
      const end = {
        uploadGroupId: props.moduleId,
        step: 'end'
      };

      if (_.isEqual(props.uploadFlowState, start)) {
        derivedState.moduleIsUploading = true;
      } else if (_.isEqual(props.uploadFlowState, end)) {
        derivedState.moduleIsUploading = false;
      }
    }

    return derivedState;
  }


  /**
   * Grid cell render
   * @param defaultRendering
   * @param cellProps
   */
  cellRender = (defaultRendering: React.ReactElement<HTMLTableCellElement> | null, cellProps: GridCellProps): JSX.Element => {
    const { dataItem, field } = cellProps;
    if (defaultRendering && field) {
      let children;
      let editedProps = {
        ...defaultRendering.props
      };

      switch (cellProps.field) {
        case this.props.selectionField:
          editedProps.className += ' tp-select-cell';
          children = this.selectCellRenderer(cellProps);
          break;
        case 'fileName':
          editedProps.className += ' tp-grid-file-name-cell';
          children = this.fileRenderer(cellProps);
          break;
        case 'fileFormat':
          const inEdit = dataItem.inEdit && this.state.editField === 'fileFormat';
          editedProps.className += ' tp-editable-cell';
          if (inEdit) {
            editedProps.className += ' tp-file-name-in-edit';
          }
          children = this.fileFormatRenderer(cellProps);
          break;
        case 'progress':
          editedProps.className += ' tp-progress-cell';
          children = this.progressRenderer((cellProps));
          break;
        default:
          children = dataItem[field];
          break;
      }

      return React.cloneElement(defaultRendering, editedProps, children);
    }
    return <></>
  };


  /**
   *  If item is selected put it into array else returns item
   * @param array
   * @param item
   */
  collocateItem = (array: any[], item: any) => {
    const { selectionField } = this.props;

    if (!item[selectionField]) return item;
    array.push({
      ...item,
      [selectionField]: false
    });
  };


  /**
   * @inheritDoc
   */
  createAlreadyUploadedData(data: any = []): FILE[] {
    const { selectionField, moveToTargets } = this.props;
    return data.map((item: any) => ({
      ...(selectionField && moveToTargets && { [selectionField]: false }),
      fileName: item.documentName || item.fileName,
      uploaded: DropUpload.UploadStatus.ALREADY_UPLOADED,
      documentId: item.documentId,
      targetId: item.targetId,
      repositoryTicket: item.repositoryTicket,
      fileFormat: {
        fileFormatId: item.fileFormatId,
        name: item.fileFormatName,
        targetFormat: _.find(this.props.projectFileFormats, { fileFormatId: item.fileFormatId })?.targetFormat
      },
      inEdit: false
    }));
  }


  /**
   * @inheritDoc
   */
  createUploadedFilesList = () => {
    const { selectionField, moveToTargets } = this.props;
    const { dragging, gridFileFormatFilter } = this.state;


    const classes = classNames("tp-uploaded-files-grid", {
      "tp-masked": dragging
    });


    const gridProps = {
      className: classes,
      data: this.filterDroppedFilesForGrid(),
      editField: 'inEdit',
      rowHeight: 32,
      cellRender: this.cellRender,
      rowRender: this.renders.rowRender,
      selectedField: selectionField,
      onHeaderSelectionChange: this.headerSelectionChange,
      onSelectionChange: this.selectionChange
    };

    let sourceFilesGridCls = classNames(
      'tp-uploaded-files-grid',
      {
        'tp-collapsed': this.props.isCollapsed
      }
    );

    let fileTypeCls = classNames(
      'tp-file-type',
      {
        'tp-filter-applied': gridFileFormatFilter
      }
    )

    return (
      <KendoGridAbstract
        {...gridProps}
        captionText={this.translate('SRL-sourceFilesCaption')}
        className={sourceFilesGridCls}
        navigatable={true}
        resizable={true}
      >
        <GridColumn
          field={selectionField}
          headerCell={this.selectHeaderCellRenderer}
          headerClassName={'tp-header-select-cell'}
          width='40px'
          resizable={false}
        />
        <GridColumn
          field="fileName"
          title={this.translate('fileName')}
          editable={false}
          minResizableWidth={116}
        />
        <GridColumn
          field="fileFormat"
          title={this.translate('fileType')}
          headerClassName={fileTypeCls}
          headerCell={this.getFileTypeMenu}
          minResizableWidth={116}
        />
        <GridColumn
          title={this.translate('fileStatus')}
          field={'progress'}
          minResizableWidth={116}
          headerCell={moveToTargets && this.statusRender}
        />
        {selectionField && moveToTargets ?
          <GridColumn
            width={'40px'}
            field={''}
            resizable={false}
            editable={false}
            cell={this.emptyCellRender}
            headerCell={this.moreOptionsRender}
          /> : null}
      </KendoGridAbstract>
    );
  };


  /**
   * Renderer for upload status section
   */
  createUploadStatusSection = () => {
    const {
      droppedFiles,
      extractArchiveEnabled,
      zipUploadInProgress
    } = this.state;
    const {
      multiBatchDialog,
      isCollapsed,
      submissionStarted,
    } = this.props;
    const disabled = droppedFiles.some((item: any) => item.selected);
    const infoBarClass = isCollapsed ? 'k-display-none' : '';

    let sourceFilesGridFooterCls = classNames(
      'tp-files-uploaded-info-bar',
      {
        'tp-collapsed': isCollapsed
      }
    );
    return (
      <div className={sourceFilesGridFooterCls}>
        {!!droppedFiles.length ?
          <>
            {this.renderFileIndicator()}
            <PuzzleToolbar
              className={`tp-info-bar-actions k-hbox k-align-items-center ${infoBarClass} tp-gap-6 !k-pr-0`}>
              {extractArchiveEnabled && !submissionStarted && !isCollapsed && this.renderExtractCheckBox()}
              {!multiBatchDialog &&
                <PuzzleButton
                  disabled={submissionStarted || !disabled}
                  onClick={this.deleteMultipleFiles}
                  iconClass={'tp-icon tp-delete'}
                  size={'small'}
                  themeColor={'primary'}
                  fillMode={'flat'}
                >
                  {this.translate('deleteFiles')}
                </PuzzleButton>
              }
              {!isCollapsed &&
                <PuzzleButton
                  disabled={submissionStarted || zipUploadInProgress}
                  iconClass={'tp-icon tp-add-circle'}
                  fillMode='flat'
                  size={'small'}
                  themeColor={'primary'}
                  onClick={this.fileUploadHandler}
                >
                  {this.translate('addMoreFiles')}
                </PuzzleButton>
              }
            </PuzzleToolbar>
          </>
          : null
        }
      </div>
    );
  };


  /**
   * Removes files form current batch
   */
  deleteMultipleFiles = () => {
    let { droppedFiles } = this.state;
    let removedItems: any = [];
    droppedFiles.filter(this.collocateItem.bind(this, removedItems));
    if (removedItems.length) {
      this.removeFile(removedItems);
    }
  };

  /**
   * Renders an empty <td/> to the last column in uploaded-files-list grid
   */
  emptyCellRender = (cellProps: GridCellProps) => {
    const tdProps = getTableTdAccessibilityProps(cellProps);
    return (
      <td {...tdProps} />
    )
  }
  /**
   * Determines which cell should be edited
   *
   * @param dataItem
   * @param field
   */
  enterEdit = (dataItem: any, field: any) => {
    const droppedFiles = this.state.droppedFiles.map((item: any) => ({
        ...item,
        inEdit: (dataItem.repositoryTicket && item.repositoryTicket === dataItem.repositoryTicket)
      })
    );
    this.setState({
      droppedFiles,
      editField: field
    });
  };

  /**
   * Disables edit mode on all cell's
   */
  exitEdit = () => {
    const droppedFiles = this.state.droppedFiles.map((item: any) => (
      { ...item, inEdit: undefined }
    ));

    this.setState({
      droppedFiles,
      field: undefined
    });
  };


  /**
   * File format cell renderer. Render value or inline editor
   */
  fileFormatRenderer = (cellProps: GridCellProps) => {
    let dataItem = cellProps.dataItem;
    let field = cellProps.field;
    let value = field ? dataItem[field] : '';
    let projectFileFormats = this.props.projectFileFormats || this.state.projectFileFormats;
    let { fileFormatIds } = dataItem;
    let parsedFileFormats = projectFileFormats.map(this.mapProjectFileFormats.bind(this, fileFormatIds));

    if (fileFormatIds) {
      let notMatchingFormatId = _.find(fileFormatIds, this.findNotMatchingFileFormats.bind(this, projectFileFormats)); // fileFormatIds.find(this.findNotMatchingFileFormats.bind(this, projectFileFormats));

      parsedFileFormats.push({
        targetFormat: null,
        fileFormatId: notMatchingFormatId,
        name: this.translate('nonParsable'),
        parsedName: this.translate('recommendedFileFormatFlag', { name: this.translate('nonParsable') })
      });
    }

    const inEdit = dataItem.inEdit && this.state.editField === 'fileFormat';
    let displayValue = {} as FILE_FORMAT;
    if (value) {
      displayValue = _.find(parsedFileFormats, { fileFormatId: value.fileFormatId }) || value;
    }
    return (
      <>
        {inEdit ? (
          <span onKeyDown={this.fileTypeComboKeyHandler.bind(this)}>
            <FilterCombo
              clearButton={false}
              defaultValue={displayValue}
              data={parsedFileFormats}
              labelClassName={'sr-only'}
              translateMethod={this.translate.bind(this)}
              dataItemKey={"fileFormatId"}
              tabIndex={0}
              size={'small'}
              textField={'parsedName'}
              onBlur={this.exitEdit}
              onClose={this.fileTypeChange.bind(this, cellProps)}
              popupSettings={{ className: this.props.zIndexCls }}
              ref={(combo: any) => combo?.comboRef.component.focus()}
              valueAsSimpleType={true}
            />
          </span>
        ) : (
          <div
            className='tp-text-ellipsis'
            tabIndex={0}
            onKeyDown={(event) => {
              const { keyCode } = event;
              if (keyCode === Keys.enter) {
                this.enterEdit(dataItem, field)
              }
            }}
            onClick={() => {
              this.enterEdit(dataItem, field)
            }}
          >
            {displayValue?.name}
          </div>
        )}
      </>
    );
  };


  /**
   * Compares selected files(source batch) and all files (targetBatch) and separates duplicates and unique files.
   * @param selectedFiles
   * @param files
   */
  findUniquesAndCopies = (selectedFiles: any[], files: any[]) => {
    const existingFiles = files.map((file: any) => file.fileName);
    let duplicates: any[] = [];
    let unique: any = [];
    selectedFiles.forEach((file: any) => {
      existingFiles.includes(file.fileName) ? duplicates.push(file) : unique.push({
        ...file,
        [this.props.selectionField]: false
      })
    });

    return {
      duplicates,
      unique
    }
  };


  /**
   * File name cell renderer.
   */
  fileRenderer = (cellProps: GridCellProps) => {
    const fileName = cellProps.dataItem.fileName;
    const parts = fileName.split('/');
    const splitFileName = parts[parts.length - 1];
    return (
      <div>
        <PuzzleButton
          aria-label={this.translate("SRL-openFilePathDialog", { fileName: fileName })}
          titleText={this.translate("SRL-openFilePathDialog", { fileName: fileName })}
          onClick={() => this.dispatch('filePath', '', { fileName })}
          fillMode={'flat'}
          themeColor={'primary'}
          size={'small'}
          iconClass={'tp-icon tp-file-path'}
          tooltipPosition={'right'}
          withTooltip
        />
        <span className={'tp-text-ellipsis'}>{splitFileName}</span>
      </div>
    )
  }


  /**
   * File type change handler. Set selected target level to file and exit edit
   */
  fileTypeChange = (cellProps: GridCellProps, event: any) => {
    let { value } = event;
    if (value) {
      let dataItem = cellProps.dataItem;
      let field = cellProps.field;
      let droppedFiles = _.cloneDeep(this.state.droppedFiles);
      let droppedLength = droppedFiles.length;
      let current;
      while (droppedLength) {
        droppedLength--;
        current = droppedFiles[droppedLength];
        if (current.repositoryTicket === dataItem.repositoryTicket && field) {
          droppedFiles[droppedLength][field] = value;
        } else if (current[this.props.selectionField] && current.repositoryTicket && field) {
          droppedFiles[droppedLength][field] = value;
        }
      }
      this.setState({
        droppedFiles: droppedFiles
      });
    }
  };


  /**
   * Exit edit mode if key ESC is pressed
   */
  fileTypeComboKeyHandler = (event: any) => {
    const { keyCode } = event;
    if (keyCode === Keys.esc) {
      this.exitEdit();
    }
  }


  /**
   * Find default file format for item
   */
  findDefaultFileFormat = (fileFormatIds: number[]) => {
    let projectFileFormats = this.props.projectFileFormats || this.state.projectFileFormats;
    let matchingFileFormats = projectFileFormats.filter((item: any) => fileFormatIds.includes(item.fileFormatId));
    let defaultFileFormat = _.find(matchingFileFormats, (item: any) => item.default);

    if (!defaultFileFormat) {
      if (matchingFileFormats.length) {
        defaultFileFormat = matchingFileFormats[0];
      } else {
        defaultFileFormat = {
          targetFormat: null,
          fileFormatId: fileFormatIds[0],
          name: this.translate('nonParsable')
        };
      }

    }

    return defaultFileFormat;
  };


  /**
   * Find id of non-parsable file format
   */
  findNotMatchingFileFormats = (projectFileFormats: any[], item: string) => {
    let projectFileFormatsLength = projectFileFormats.length;
    let current;
    let match;
    while (projectFileFormatsLength && !match) {
      projectFileFormatsLength--;
      current = projectFileFormats[projectFileFormatsLength];
      match = current.fileFormatId === item;
    }
    return !match;
  };


  /**
   * Filter and select files with choosen file format
   */
  filterAndSelectFiles = (event: DropDownButtonItemClickEvent) => {
    const { fileFormatId } = event.item;
    this.setState({
      gridFileFormatFilter: fileFormatId
    }, () => this.headerSelectionChange({ value: true, fromFilter: true }));
  };


  /**
   * Filter dropped files if gilter file format is selected
   */
  filterDroppedFilesForGrid = () => {
    let { droppedFiles, gridFileFormatFilter } = this.state;
    let filteredFiles = droppedFiles;
    if (gridFileFormatFilter) {
      filteredFiles = droppedFiles.filter((item: FILE) => item.fileFormat?.fileFormatId === gridFileFormatFilter);
    }

    return filteredFiles;
  }


  /**
   * Getter for droppedFiles, deletedFiles and submissionFolder
   */
  getData = () => {
    const {
      droppedFiles,
      deletedFiles,
      submissionFolder
    } = this.state;

    return { droppedFiles, deletedFiles, submissionFolder };
  };

  /**
   * Generate file type menu
   */
  getFileTypeMenu = () => {
    let fileFormats = this.props.projectFileFormats || this.state.projectFileFormats;
    return (
      <PuzzleDropDownButton
        popupSettings={{
          animate: false,
          popupClass: "tp-file-types-popup",
        }}
        className={'tp-file-type-btn'}
        fillMode={'flat'}
        iconClass={'tp-icon tp-arrow-chevron-down'}
        items={[{ name: this.translate('allFileTypes') }].concat(fileFormats)}
        itemRender={(props: any) => {
          return <div>{props.item.name}</div>
        }}
        onItemClick={this.filterAndSelectFiles}
        text={this.translate('fileType')}
      />
    );
  };


  /**
   * @inheritDoc
   */
  getInstructions = (showInstructions: boolean = false): JSX.Element | null => {
    let render = showInstructions ?
      <div className="tp-drag-and-drop-formats-supported">
        {this.translate('sourceFilesInstructions')}
      </div>
      : null;
    return render;
  };

  getTargetFormat = (): string => {
    return this.state.droppedFiles.reduce((acc: string | null, curr: any) => {
      if (acc?.length && acc !== curr.fileFormat.targetFormat) {
        acc = null;
      } else {
        acc = curr.fileFormat.targetFormat;
      }
      return acc;
    }, '');
  };

  /**
   * Returns new progresses state property that is merged old property and new based on dropped files
   * @param droppedFiles
   */
  getUploadProgressByStateAndDroppedFiles(droppedFiles: any) {
    let progresses: any = { ...this.state.progresses };
    droppedFiles.forEach((fileItem: any) => {
      if (fileItem.fileName && !progresses[fileItem.fileName]) {
        progresses[fileItem.fileName] = fileItem.uploaded === SourceFiles.UploadStatus.UPLOADED ? 100 : 0;
      }
    });
    return progresses;
  }


  /**
   * Returns  all selected files
   */
  getSelectedFiles = () => {
    const { selectionField } = this.props;
    let { droppedFiles } = this.state;
    let selectedFiles = droppedFiles.filter((file: any) => file[selectionField]);
    return selectedFiles
  };


  /**
   * Forwards event to default grid selection change handler
   */
  handleSelectionChange = (selectionHandler: Function | undefined, dataItem: FILE, syntheticEvent: any) => {
    let { droppedFiles } = this.state;
    const current = droppedFiles.findIndex((item: any) => item === dataItem);
    let last = this.lastSelectedIndex;
    if (!syntheticEvent.nativeEvent.shiftKey) {
      this.lastSelectedIndex = last = current;
    }
    const select = !dataItem.selected;
    for (let i = Math.min(last, current); i <= Math.max(last, current); i++) {
      if (droppedFiles[i].repositoryTicket !== dataItem.repositoryTicket || droppedFiles[i].documentId !== dataItem.documentId) {
        droppedFiles[i].selected = select;
      }
    }
    if (selectionHandler) {
      selectionHandler({ syntheticEvent });
    }
  };


  /**
   * Header selection handler
   * @param event
   */
  headerSelectionChange = (event: any) => {
    let checked = event.value;
    const { isDisabled } = this.props;
    let { droppedFiles } = this.state;
    let filteredGridFiles = this.filterDroppedFilesForGrid();
    if (!isDisabled) {
      droppedFiles = droppedFiles.map((item: FILE) => {
        return {
          ...item,
          [this.props.selectionField]: event.fromFilter ? _.includes(filteredGridFiles, item) : checked
        };
      });
    }
    const progresses = this.getUploadProgressByStateAndDroppedFiles(droppedFiles);
    this.setState({
      progresses,
      droppedFiles
    });
  };


  /**
   * Map project file formats
   */
  mapProjectFileFormats = (fileFormatIds: any[], item: any) => {
    let newItem = { ...item }; //break reference
    if (fileFormatIds && fileFormatIds.includes(item.fileFormatId)) {
      newItem.parsedName = this.translate('recommendedFileFormatFlag', { name: newItem.name });
    } else {
      newItem.parsedName = newItem.name;
    }
    return newItem;
  };


  /**
   * More options render
   */
  moreOptionsRender = () => {
    const { isDisabled } = this.props;
    const enabled = this.state.droppedFiles.some((item: any) => item.selected) && !isDisabled;
    return (
      <div className='tp-file-status-wrapper k-menu-primary k-d-flex k-justify-content-center k-align-items-center'>
        <Menu hoverOpenDelay={300} hoverCloseDelay={200} vertical={true} onSelect={(e) => {
          const data = e.item.data;
          if (data) {
            if (data.operation) {
              this.deleteMultipleFiles()
            } else {
              this.moveFiles(data.moduleId)
            }
          }
        }} ref={(element) => this.menuRef = element}>
          <MenuItem
            disabled={!enabled}
          >
            <MenuItem
              text={this.translate('deleteFiles')}
              data={{ operation: 'delete' }}
            />
            <MenuItem
              text={this.translate('moveFiles')}
              disabled={!this.props.moveToTargets.filter((item: any) => item.name).length}
            >
              {this.renderMenuBatchNames()}
            </MenuItem>
          </MenuItem>
        </Menu>
      </div>

    )
  };


  /**
   * Moves files from current to selected batch.
   * @param selectedBatch
   */
  moveFiles = (selectedBatch?: string) => {
    this.dispatch('moveFiles', '', {
      moduleId: selectedBatch,
      forceAdd: true,
      removeFrom: this.moduleId
    })
  };


  /**
   * ProgressBar cell renderer
   */
  progressRenderer = (cellProps: GridCellProps) => {
    const { progresses = {} } = this.state;
    let { moduleId } = this.props;
    const uploadGroupId = moduleId;
    return (
      <>
        <div className={'tp-progress-cell-inner-wrap'}>
          <ProgressBar
            key={`ProgressBar_${uploadGroupId}_${cellProps.dataItem.fileName}`}
            className={'tp-uploaded-list-item-status k-text-ellipsis'}
            file={cellProps.dataItem}
            fileName={cellProps.dataItem.fileName}
            group={uploadGroupId}
            progresses={progresses}
          />
        </div>
      </>
    )
  };


  renderFileIndicator = () => {
    const { batch, submissionStatus, partiallyAnalyzed } = this.props;
    const { droppedFiles, moduleIsUploading, uploaded: numberOfFiles } = this.state;
    const hasAddedFile = droppedFiles.some((file: any) => file.uploaded !== 'alreadyUploaded');
    hasAddedFile && this.dispatch('filesChanged');
    const notAnalyzed = this.props.submissionStatus === "WAITING";
    const preExpandJob = { batchName: batch?.name, targetLanguage: ''};
    const indicator = notAnalyzed || hasAddedFile ?
      <span className={'tp-files-uploaded-info tp-text-regular'}>{this.translate('filesNotAnalyzed')}</span>
      : <WordCountLink data={{ ...batch, status: { name: submissionStatus }, partiallyAnalyzed }} groupBy='languages'
                       preExpandJob={preExpandJob} />;

    return (
      <>
        <Container
          hBox
          alignItemsCenter
          wrap
          className={'tp-files-counter tp-text-semibold tp-font-size-sm'}
        >
          {
            !moduleIsUploading ? (
              <>
                <div className="tp-icon tp-icon-sm tp-check-circle tp-color-success" />
                {this.translate('filesFound', { numberOfFiles })}
                <>
                  <span className="tp-space-right-1 tp-space-left-half-1">|</span>
                  {indicator}
                </>
              </>
            ) : (
              <>
                <div className="tp-icon tp-files-uploading tp-color-accent" />
                <span className={'tp-files-uploaded-info tp-text-regular'}>{this.translate('uploading')}</span>
              </>
            )
          }
        </Container>
      </>
    );
  };


  /**
   * Renders Batch Names
   */
  renderMenuBatchNames = () => {
    const menuItems = this.props.moveToTargets.map((item: any, index: number) => {
      return item.name && (<MenuItem
        key={"menuItem_1_" + index}
        text={item.name}
        data={item}
      />)
    });
    return menuItems;
  };


  /**
   * Selection cell render
   * @param cellProps
   */
  selectCellRenderer = (cellProps: GridCellProps) => {
    const { submissionStarted } = this.props;
    const { dataItem, selectionChange } = cellProps;
    return (
      <PuzzleCheckbox
        label={this.translate('SRL-checkboxFileName', { fileName: dataItem.fileName })}
        disabled={submissionStarted}
        checked={!!dataItem[this.props.selectionField]}
        onChange={this.handleSelectionChange.bind(this, selectionChange, dataItem)}
      />
    )
  };


  /**
   *
   * @param event
   */
  selectionChange = (event: any) => {
    const { selectionField } = this.props;
    let currentDataItem = event.dataItem;
    let { droppedFiles } = this.state;
    let arrayIndex = droppedFiles.findIndex((currentValue: any) => currentValue === currentDataItem);
    let item = droppedFiles[arrayIndex];
    item[selectionField] = !currentDataItem[selectionField];

    this.setState({
      droppedFiles
    })
  };


  /**
   * Custom header selection cell
   */
  selectHeaderCellRenderer = () => {
    const { droppedFiles } = this.state;
    const { selectionField, submissionStarted } = this.props;
    const isHeaderSelected = droppedFiles.every((item: any) => item[selectionField]);

    return (
      <PuzzleCheckbox
        checked={isHeaderSelected}
        disabled={submissionStarted}
        label={this.translate("selectAllCell")}
        onChange={this.headerSelectionChange}
      />
    );
  }


  /**
   * DroppedFiles setter
   * @param uniqueItems
   */
  setDroppedFiles = (uniqueItems: any) => {
    let { droppedFiles } = this.state;
    const { selectionField } = this.props;
    const uniqueFilesNames = uniqueItems.map((item: any) => {
      return item.fileName
    });
    let items = droppedFiles.filter((item: any) => {
      return !uniqueFilesNames.includes(item.fileName);
    }).map((file: any) => {
      return {
        ...file,
        [selectionField]: false
      }
    });
    this.setState({
      droppedFiles: items,
      ...(this.totalUploaded(items))
    });
  };


  /**
   * @inheritDoc
   */
  setExistingFiles = (files: any, details: any, fileFormats: any[]): void => {
    const { project } = details;
    const existingFiles = files ? this.createAlreadyUploadedData(files) : [];
    this.setState({
      allowUpload: !!project,
      projectId: project,
      droppedFiles: existingFiles,
      dragging: false,
      uploaded: existingFiles.length,
    });
  };

  /**
   * Set project file formats
   */
  setProjectFileFormats = (projectFileFormats: any[]) => {
    this.setState({
      projectFileFormats
    })
  };


  /**
   * Renders status column
   */
  statusRender = () => {
    return (
      <div className='tp-file-status-wrapper k-menu-primary k-d-flex k-align-items-center k-justify-content-center'>
        <div className={'tp-file-status'}>{this.translate('fileStatus')}</div>
      </div>
    )
  }


  /**
   * Updates data about uploading files, upon setting state calls next upload
   * @param updatedFiles
   * @param folder
   * @param forceAdd
   * @param lastUploaded
   */
  updateStateAndContinueUpload = (updatedFiles: FILE[], folder: string, forceAdd: boolean, lastUploaded: FILE[]) => {
    let parsedUploaded: FILE[] = [];
    if (updatedFiles && !forceAdd) {
      parsedUploaded = updatedFiles.map((item: FILE) => {
        if (this.props.selectionField && this.props.moveToTargets) {
          //@ts-ignore
          item[this.props.selectionField] = false
        }
        if (!item.fileFormat && item.fileFormatIds) {
          let defaultFileFormat = this.findDefaultFileFormat(item.fileFormatIds);
          item.fileFormat = defaultFileFormat;
        }
        return {
          ...item,
          isAlreadyUpload: true
        };
      });
    }

    const droppedFiles = forceAdd ? this.state.droppedFiles.concat(updatedFiles) : parsedUploaded;
    const {
      failed,
      uploaded
    } = this.totalUploaded(droppedFiles);
    // @ts-ignore
    this.setState({
      ...(folder && { submissionFolder: folder }),
      dragging: false,
      droppedFiles,
      failed,
      uploaded,
      zipUploadInProgress: false
    }, () => {
      this.setAnnouncements(ANNOUNCEMENTS_TYPE.UPLOAD, lastUploaded);
    })
  };


}

export default React.memo(SourceFiles); //PDII-36870
